/* Compliance client — cached fetch of the public catalog and per-feature helpers.
 *
 * Exposed hooks:
 *   useComplianceCatalog()                    — { catalog, loading }
 *   useDisclaimer(context)                    — { variant, text }
 *   useFeatureDisclaimer(featureId)           — { variant, text }
 *   useConsentStatus()                        — { status, loading, refresh }
 *   acceptConsent(items)                      — POST helper
 */
import { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";

let _catalogCache = { bg: null, en: null };

export function useComplianceCatalog() {
  const { API } = useAuth();
  const { language } = useLanguage();
  const lang = language === "en" ? "en" : "bg";
  const [catalog, setCatalog] = useState(_catalogCache[lang]);
  const [loading, setLoading] = useState(!_catalogCache[lang]);

  useEffect(() => {
    if (_catalogCache[lang]) { setCatalog(_catalogCache[lang]); setLoading(false); return; }
    setLoading(true);
    axios.get(`${API}/compliance/catalog?lang=${lang}`)
      .then(({ data }) => { _catalogCache[lang] = data; setCatalog(data); })
      .catch(() => setCatalog(null))
      .finally(() => setLoading(false));
  }, [API, lang]);

  return { catalog, loading };
}

export function useDisclaimer(context) {
  const { catalog } = useComplianceCatalog();
  return useMemo(() => {
    if (!catalog?.disclaimers) return { variant: "light", text: "" };
    return catalog.disclaimers[context] || catalog.disclaimers.general || { variant: "light", text: "" };
  }, [catalog, context]);
}

export function useFeatureDisclaimer(featureId) {
  const { catalog } = useComplianceCatalog();
  const context = catalog?.feature_context_map?.[featureId] || "general";
  return useDisclaimer(context);
}

export function useConsentStatus() {
  const { API, user } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const refresh = useCallback(async () => {
    if (!user) { setStatus(null); setLoading(false); return; }
    try {
      setLoading(true);
      const { data } = await axios.get(`${API}/compliance/consent-status`);
      setStatus(data);
    } catch { setStatus(null); } finally { setLoading(false); }
  }, [API, user]);
  useEffect(() => { refresh(); }, [refresh]);
  return { status, loading, refresh };
}

export async function acceptConsent(API, items) {
  const { data } = await axios.post(`${API}/compliance/accept`, { items });
  return data;
}

export function clearComplianceCache() { _catalogCache = { bg: null, en: null }; }
